import * as legacyGroupsClient from '../apis/groupsAPI';
import type { LegacyGetGroupsQuery as LegacyGetGroupsQuery } from '../apis/groupsAPI/types';
import type { getGroupById, getGroups, GroupData, getGroupMembers } from './groups';
import { legacyImageUrlsMapper, legacyPhoneNumbersMapper } from './legacyCommonMappers';

export async function getLegacyGroupById({
  groupId,
}: {
  groupId: number | null;
}): ReturnType<typeof getGroupById> {
  if (!groupId) {
    return {
      error: {
        status: 'error',
        error: {
          httpStatus: 400,
          code: 'BAD_REQUEST',
          details: {
            issues: {
              paramsResult: null,
              headersResult: null,
              queryResult: null,
              bodyResult: null,
            },
          },
        },
      },
      response: {} as Response,
    };
  }

  const res = await legacyGroupsClient.getGroupById(groupId);

  const accountId = res.subOrganizationID ? res.subOrganizationID : res.organizationID;
  const accountName = res.subOrganizationname ? res.subOrganizationname : res.organizationName;
  const tenantId = res.organizationID;

  if (!res || !accountId || !accountName || !tenantId) {
    return {
      error: {
        status: 'error',
        error: {
          httpStatus: 400,
          code: 'BAD_REQUEST',
          details: {
            issues: {
              paramsResult: null,
              headersResult: null,
              queryResult: null,
              bodyResult: null,
            },
          },
        },
      },
      response: {} as Response,
    };
  }

  return {
    data: {
      status: 'success',
      data: {
        account: {
          id: accountId,
          name: accountName,
          tenantId,
        },
        id: res.id,
        imageUrls: legacyImageUrlsMapper.fromLegacyToCore(res.imageFileName),
        membersCount: res.membersCount,
        name: res.name,
        type: groupTypeToTypeMapper[res.groupType],
        // TODO: Should remove "groupType" in favor of only using "type"
        groupType: res.groupType,
        // TODO: Should use "imageUrls" instead of "imageFileName"
        imageFileName: res.imageFileName,
        // TODO: Handle when consuming GET groups from core-api
        groupMembersCount: res.groupMembersCount,
        member: res.member,
        organizationID: res.organizationID,
        organizationName: res.organizationName,
        subOrganizationID: res.subOrganizationID,
        subOrganizationname: res.subOrganizationname,
        menuItems: res.menuItems,
      },
    },
    response: {} as Response,
  };
}

export async function getLegacyGroups(query?: LegacyGetGroupsQuery): ReturnType<typeof getGroups> {
  try {
    const result = await legacyGroupsClient.getGroups(query);

    return {
      data: {
        status: 'success',
        data: result
          .filter(res => {
            const accountId = res.subOrganizationID ? res.subOrganizationID : res.organizationID;
            const accountName = res.subOrganizationname
              ? res.subOrganizationname
              : res.organizationName;
            const tenantId = res.organizationID;

            if (accountId && accountName && tenantId) return true;
          })
          .map(res => {
            const accountId = res.subOrganizationID ? res.subOrganizationID : res.organizationID;
            const accountName = res.subOrganizationname
              ? res.subOrganizationname
              : res.organizationName;
            const tenantId = res.organizationID;

            return {
              account: {
                id: accountId as number,
                name: accountName as string,
                tenantId: tenantId as number,
              },
              id: res.id,
              imageUrls: legacyImageUrlsMapper.fromLegacyToCore(res.imageFileName),
              membersCount: res.membersCount,
              name: res.name,
              type: res.groupType,
              // TODO: Should remove "groupType" in favor of only using "type"
              groupType: res.groupType,
              // TODO: Should use "imageUrls" instead of "imageFileName"
              imageFileName: res.imageFileName,
              // TODO: Handle when consuming GET groups from core-api
              groupMembersCount: res.groupMembersCount,
              member: res.member,
              organizationID: res.organizationID,
              organizationName: res.organizationName,
              subOrganizationID: res.subOrganizationID,
              subOrganizationname: res.subOrganizationname,
              menuItems: res.menuItems,
            };
          }),
        metadata: {
          pagination: {
            totalCount: result.length,
            offset: 0,
            limit: result.length,
          },
        },
      },
      response: {} as Response,
    };
  } catch (ignoreError) {
    return {
      error: {
        status: 'error',
        error: {
          httpStatus: 400,
          code: 'BAD_REQUEST',
          details: {
            issues: {
              paramsResult: null,
              headersResult: null,
              queryResult: null,
              bodyResult: null,
            },
          },
        },
      },
      response: {} as Response,
    };
  }
}

export async function getLegacyGroupMembers({
  groupId,
}: {
  groupId: number;
}): ReturnType<typeof getGroupMembers> {
  const res = await legacyGroupsClient.getGroupMembers({ id: groupId });

  if (!res) {
    return {
      error: {
        status: 'error',
        error: {
          httpStatus: 400,
          code: 'BAD_REQUEST',
          details: {
            issues: {
              paramsResult: null,
              headersResult: null,
              queryResult: null,
              bodyResult: null,
            },
          },
        },
      },
      response: {} as Response,
    };
  }
  return {
    data: {
      status: 'success',
      data: res.map(member => ({
        id: member.userID,
        name: member.userName || '',
        title: member.info,
        info: member.info,
        userID: member.userID,
        userName: member.userName,
        userEmail: member.userEmail,
        admin: member.admin,
        lastAccess: member.lastAccess,
        unavailableDateTo: member.unavailableDateTo,
        imageUrls: legacyImageUrlsMapper.fromLegacyToCore(member.photoFileName),
        email: member.userEmail || '',
        photoFileName: member.photoFileName, //TODO: Should use "imageUrls" instead of "photoFileName"
        phoneNumbers: legacyPhoneNumbersMapper.fromLegacyToCore(member.phoneNumber),
        lastAccessAt: member.lastAccess,
        unavailableUntil: member.unavailableDateTo,
        isAdmin: member.admin,
        groupID: member.groupID,
      })),
      metadata: {
        pagination: {
          totalCount: res.length,
          offset: 0,
          limit: res.length,
        },
      },
    },
    response: {} as Response,
  };
}

const groupTypeToTypeMapper: Record<0 | 2 | 3 | 4, GroupData['type']> = {
  0: 'default',
  2: 'hidden',
  3: 'crossOrg',
  4: 'coAlert',
};

export const typeToGroupTypeMapper: Record<GroupData['type'], 0 | 2 | 3 | 4> = {
  default: 0,
  hidden: 2,
  crossOrg: 3,
  coAlert: 4,
};

export const functionToMenuItemMapper = {
  checklists: 'checklists',
  documents: 'documents',
  panic_button: 'group_alarm',
  lone_worker_timer: 'group_alarm_work_alone',
  im_ok: 'muster',
};
